import { serializeSearchFiltersQueryValue } from '@/shared/components/filters/searchFiltersQuery';

interface CompanyResultColumnFilter {
    label: string;
    path: string[];
    value: unknown;
}

interface CompanyResultColumnFilterLink extends CompanyResultColumnFilter {
    href: string;
}

const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
] as const;

const asRecord = (value: unknown): Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const setValueAtPath = (
    currentValue: Record<string, unknown>,
    [key, ...remainingPath]: string[],
    value: unknown,
): Record<string, unknown> => {
    if (!key) {
        return currentValue;
    }

    return {
        ...currentValue,
        [key]: remainingPath.length ? setValueAtPath(asRecord(currentValue[key]), remainingPath, value) : value,
    };
};

const appendUnique = (currentValue: unknown, nextValue: string) =>
    Array.from(new Set([...(Array.isArray(currentValue) ? currentValue.map(String) : []), nextValue]));

const finiteNumber = (value: unknown): number | undefined => {
    const numericValue = Number(value);

    return Number.isFinite(numericValue) ? numericValue : undefined;
};

const employeeRangeFor = (value: number): string | undefined => {
    if (value >= 1 && value <= 10) {
        return '1 - 10';
    }
    if (value <= 50) {
        return '11 - 50';
    }
    if (value <= 250) {
        return '51 - 250';
    }
    if (value <= 1_000) {
        return '251 - 1k';
    }
    if (value <= 5_000) {
        return '1k - 5k';
    }
    if (value <= 10_000) {
        return '5k - 10k';
    }
    if (value <= 50_000) {
        return '10k - 50k';
    }
    if (value <= 100_000) {
        return '50k - 100k';
    }
    if (value > 100_000) {
        return '100k+';
    }

    return undefined;
};

const humanizeTrafficRank = (value: string) =>
    value
        .split('_')
        .filter(Boolean)
        .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
        .join(' ');

/**
 * Converts a rendered result-column value into the same path/value pair used
 * by the Companies left-sider and shared filter row.
 */
export const getCompanyResultColumnFilter = (
    column: string,
    result: unknown,
    currentFilters: Record<string, unknown> = {},
): CompanyResultColumnFilter | undefined => {
    const company = asRecord(result);
    const enrichment = asRecord(company.enrichment);
    const clearbit = asRecord(enrichment.clearbit);
    const clearbitGeo = asRecord(clearbit.geo);
    const clearbitMetrics = asRecord(clearbit.metrics);

    const append = (path: string[], currentValue: unknown, value: unknown, label = String(value)) => {
        if (value === undefined || value === null || value === '') {
            return undefined;
        }

        return {
            label,
            path,
            value: appendUnique(currentValue, String(value)),
        } satisfies CompanyResultColumnFilter;
    };

    if (column === 'exchange') {
        const value = enrichment.exchange ?? company.exchange;

        return append(['exchanges'], currentFilters.exchanges, value);
    }

    if (column === 'industry') {
        return append(['industries'], currentFilters.industries, enrichment.industry);
    }

    if (column === 'sector') {
        return append(['sectors'], currentFilters.sectors, enrichment.sector);
    }

    const currentLocation = asRecord(currentFilters.location);

    if (column === 'country') {
        const value = enrichment.country ?? clearbitGeo.countryCode ?? clearbitGeo.country;
        const label = clearbitGeo.country ?? value;

        return value
            ? {
                  label: String(label),
                  path: ['location'],
                  value: { ...currentLocation, country: String(value) },
              }
            : undefined;
    }

    const locationFieldMap: Record<string, string> = {
        city: 'city',
        state: 'stateTerritory',
        zip: 'zipCode',
    };
    const locationField = locationFieldMap[column];

    if (locationField) {
        const value = enrichment[column];

        return value
            ? {
                  label: String(value),
                  path: ['location'],
                  value: { ...currentLocation, [locationField]: String(value) },
              }
            : undefined;
    }

    if (column === 'fullTimeEmployees') {
        const employeeCount = finiteNumber(enrichment.fullTimeEmployees);
        const range = employeeCount === undefined ? undefined : employeeRangeFor(employeeCount);

        return range ? append(['employeeHeadcount'], currentFilters.employeeHeadcount, range, range) : undefined;
    }

    const rangeFieldMap: Record<string, { source: unknown; target: string }> = {
        beta: { source: enrichment.beta, target: 'beta' },
        lastDiv: { source: enrichment.lastDiv, target: 'dividend' },
        mktCap: { source: enrichment.mktCap ?? enrichment.marketCap, target: 'marketCap' },
        price: { source: enrichment.price, target: 'price' },
    };
    const rangeField = rangeFieldMap[column];

    if (rangeField) {
        const value = finiteNumber(rangeField.source);

        return value === undefined
            ? undefined
            : {
                  label: value.toLocaleString(),
                  path: ['screener', rangeField.target],
                  value: { max: value, min: value },
              };
    }

    if (column === 'fiscalYearEnd') {
        const monthNumber = finiteNumber(clearbitMetrics.fiscalYearEnd);
        const month = monthNumber === undefined ? undefined : MONTHS[monthNumber - 1];

        return month ? append(['fiscalYearEnd'], currentFilters.fiscalYearEnd, month, month) : undefined;
    }

    if (column === 'trafficRank' && typeof clearbitMetrics.trafficRank === 'string') {
        const rank = humanizeTrafficRank(clearbitMetrics.trafficRank);

        return append(['webTraffic'], currentFilters.webTraffic, rank, rank);
    }

    return undefined;
};

export const getCompanyResultColumnFilterLink = (
    column: string,
    result: unknown,
    currentFilters: Record<string, unknown> = {},
): CompanyResultColumnFilterLink | undefined => {
    const filter = getCompanyResultColumnFilter(column, result, currentFilters);

    if (!filter) {
        return undefined;
    }

    const filters = setValueAtPath(currentFilters, filter.path, filter.value);
    const searchParams = new URLSearchParams({ filters: serializeSearchFiltersQueryValue(filters) });

    return {
        ...filter,
        href: `/search/companies?${searchParams.toString()}`,
    };
};

export type { CompanyResultColumnFilter, CompanyResultColumnFilterLink };
